/**
 * AI KUZATUVI — analitika va to'liq chaqiruvlar jurnali.
 *
 * NIMA UCHUN SINOV CHATI OLIB TASHLANDI
 * Sahifaning chap yarmini "Sinov chati" egallab turardi: u AI bilan
 * Telegram'siz gaplashish imkonini berardi, lekin bitta savolga ham javob
 * bermasdi — bot PRODDA nima qilyapti. Kuzatuv sahifasi kuzatish uchun:
 * o'sha joyda endi analitika turadi. Backenddagi `POST /ai/playground`
 * ATAYLAB qoldirilgan (tarixiy `kind="playground"` yozuvlari jurnalda
 * ko'rinib turadi), faqat panel qismi olib tashlandi.
 *
 * IKKI EKRAN, BITTA MANBA
 *   Analitika — `GET /ai/analytics` (bitta so'rov, 12 blok) + `GET /ai/stats`
 *               (til kesimi). Kun chelaklari UTC+5 — admin ko'rgan "kun".
 *   Jurnal    — `GET /ai/logs`, HAMMA turlar (reply, tmpl_pick, categorize,
 *               voice, test, playground, faq_suggest). Ilgari panel faqat
 *               reply/playground ni ko'rsatardi va voronkaning yarmi
 *               ko'rinmasdi.
 *
 * POLLING ARZON: `include_prompt` BERILMAYDI — 30 000 belgilik system prompt
 * ro'yxat bilan birga kelmaydi. To'liq prompt faqat yozuv ochilganda,
 * `GET /ai/logs/{id}` orqali keladi.
 *
 * YANGI QATORLAR SAQLANADI: matn hech qayerda `.replace(/\n/g,' ')` bilan
 * tekislanmaydi — `TextViewer` o'zi o'raydi va skrollaydi.
 */

import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, Tooltip as RcTooltip, XAxis, YAxis,
} from 'recharts'
import {
  HiArrowPath, HiBanknotes, HiBookOpen, HiChartBar, HiChatBubbleLeftRight,
  HiClock, HiCodeBracket, HiCog6Tooth, HiCpuChip, HiExclamationTriangle,
  HiFlag, HiFunnel, HiLanguage, HiListBullet, HiOutlineFlag, HiRectangleStack,
  HiSparkles, HiSquares2X2, HiUser,
} from 'react-icons/hi2'

import { aiApi } from '../api'
import { useStore } from '../store'
import { ErrorBox } from '../hooks/useApi.jsx'
import TrendChart from '../components/TrendChart'
import { ChartFrame, ChartTooltip, toneColor, useThemeTokens } from '../components/chart.jsx'
import {
  Alert, Badge, Button, Card, Checkbox, DescriptionList, DistributionBar, Drawer,
  EmptyState, Field, Funnel, Input, KpiStrip, Loader, Modal, Pagination, Progress,
  SearchInput, Segmented, Select, Table, Tabs, TextViewer, Toolbar,
} from '../ui'

/* ─────────────────────────────────────────────────────────────
   Doimiylar va formatlash
   ───────────────────────────────────────────────────────────── */

const LANG_LABEL = { uz: "O'zbek", uz_cy: 'Кирилл', ru: 'Русский' }

// Alifbo kalitlari backenddan shu nomlar bilan keladi (`_ai_script_pair`)
const SCRIPT_LABEL = { lat: 'Lotin', cyr: 'Kirill', noaniq: 'Aniqlanmadi' }

const PERIODS = [
  { value: 7, label: '7 kun' },
  { value: 14, label: '14 kun' },
  { value: 30, label: '30 kun' },
  { value: 90, label: '90 kun' },
]

const LOG_DAY_OPTIONS = [
  { value: '', label: 'Butun tarix' },
  { value: '1', label: 'Bugun' },
  { value: '7', label: '7 kun' },
  { value: '30', label: '30 kun' },
  { value: '90', label: '90 kun' },
]

const LANG_OPTIONS = [
  { value: '', label: 'Barcha tillar' },
  { value: 'uz', label: "O'zbek" },
  { value: 'uz_cy', label: 'Кирилл' },
  { value: 'ru', label: 'Русский' },
]

const PER_PAGE = 30
// Jurnal 60 soniyada yangilanadi: 15 soniya prompt bilan birga kelganda
// kerak edi, endi qator yengil va bunchalik tez-tez so'rash ortiqcha.
const POLL_MS = 60000

// Kunlik grafikdagi ko'rsatkichlar — bitta grafik, tanlanadigan seriya.
// Ranglar `toneColor()` orqali mavjud status tokenlaridan olinadi.
const TREND_METRICS = {
  hajm: {
    label: 'Hajm',
    series: [
      { key: 'calls', name: 'Chaqiruv', tone: 'ink' },
      { key: 'replies', name: 'Javob', tone: 'ok' },
    ],
  },
  narx: {
    label: 'Narx',
    series: [{ key: 'cost_usd', name: 'Xarajat', tone: 'ink' }],
    fmt: (v) => fmtUsd(v),
  },
  sifat: {
    label: 'Sifat',
    series: [
      { key: 'flagged', name: 'Belgilangan', tone: 'medium' },
      { key: 'failed', name: 'Xato', tone: 'critical' },
    ],
  },
  token: {
    label: 'Token',
    series: [
      { key: 'input_tokens', name: 'Kirish', tone: 'ink' },
      { key: 'output_tokens', name: 'Chiqish', tone: 'muted' },
    ],
    fmt: (v) => fmtNum(v),
  },
  kesh: {
    label: 'Kesh',
    series: [{ key: 'cache_pct', name: 'Kesh ulushi', tone: 'ok' }],
    fmt: (v) => `${v}%`,
  },
}

const PROMPT_TABS = [
  { id: 'io', label: "So'rov va javob", icon: HiChatBubbleLeftRight },
  { id: 'system', label: 'System prompt', icon: HiCog6Tooth },
  { id: 'messages', label: 'Suhbat', icon: HiUser },
  { id: 'raw', label: 'Raw JSON', icon: HiCodeBracket },
]

function fmtTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const p = (n) => String(n).padStart(2, '0')
  return `${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`
}

/** Ming ajratgichi — ingichka bo'shliq bilan (tabular-nums bilan birga o'qiladi). */
function fmtNum(n) {
  return String(Math.round(Number(n) || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

/** Token: uzun raqam qatorni kengaytirib yuboradi, shuning uchun qisqartiriladi. */
function fmtTok(n) {
  const v = Number(n) || 0
  if (v >= 1000000) return `${(v / 1000000).toFixed(1).replace('.', ',')}M`
  if (v >= 1000) return `${(v / 1000).toFixed(1).replace('.', ',')}k`
  return String(v)
}

/** Narx: sentdan kichik summalar ham ko'rinsin uchun kasr soni suzuvchi. */
function fmtUsd(v) {
  const n = Number(v) || 0
  if (n === 0) return '$0'
  if (n >= 1) return `$${n.toFixed(2)}`
  if (n >= 0.01) return `$${n.toFixed(4)}`
  return `$${n.toFixed(5)}`
}

function fmtPct(v) {
  if (v == null) return '—'
  return `${String(v).replace('.', ',')}%`
}

/** "2026-08-03" → "03.08" — X o'qi yozuvlari qisqa bo'lishi uchun. */
function dayLabel(d) {
  return typeof d === 'string' && d.length >= 10 ? `${d.slice(8, 10)}.${d.slice(5, 7)}` : (d || '')
}

/* ─────────────────────────────────────────────────────────────
   Sahifa
   ───────────────────────────────────────────────────────────── */

export default function AIMonitoring() {
  const addToast = useStore((s) => s.addToast)

  const [tab, setTab] = useState('stats')          // stats | log
  const [days, setDays] = useState(30)

  // ── Analitika ──
  const [analytics, setAnalytics] = useState(null)
  const [stats, setStats] = useState(null)
  const [aLoading, setALoading] = useState(true)

  // ── Jurnal filtrlari ──
  const [logs, setLogs] = useState(null)
  const [lLoading, setLLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [kinds, setKinds] = useState([])           // bo'sh = HAMMA turlar
  const [onlyFlagged, setOnlyFlagged] = useState(false)
  const [onlyFailed, setOnlyFailed] = useState(false)
  const [lang, setLang] = useState('')
  const [logDays, setLogDays] = useState('')
  const [query, setQuery] = useState('')
  const [ticket, setTicket] = useState('')

  // Ikki manba — ikki xato. Bitta `error` holati bo'lganda jurnal muvaffaqiyatli
  // yuklanishi analitika xatosini jimgina o'chirib yuborardi va ekranda sababsiz
  // "Analitika hozircha yo'q" qolardi.
  const [aError, setAError] = useState(null)
  const [lError, setLError] = useState(null)

  // ── Tafsilot paneli ──
  const [detail, setDetail] = useState(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)

  // ── Bayroqcha ──
  const [busyId, setBusyId] = useState(null)
  const [flagTarget, setFlagTarget] = useState(null)
  const [flagNote, setFlagNote] = useState('')
  const flagNoteId = useId()

  const loadAnalytics = useCallback(async (silent = false) => {
    if (!silent) setALoading(true)
    try {
      // `stats` faqat til kesimi uchun; u yiqilsa analitika baribir ko'rinadi.
      const [an, st] = await Promise.all([
        aiApi.analytics(days),
        aiApi.stats(days, 'all').catch(() => null),
      ])
      setAnalytics(an)
      setStats(st)
      setAError(null)
    } catch (e) {
      setAError(e.message || "Analitika yuklanmadi")
    } finally {
      if (!silent) setALoading(false)
    }
  }, [days])

  const logParams = useMemo(() => {
    const p = { skip: (page - 1) * PER_PAGE, limit: PER_PAGE }
    if (kinds.length) p.kinds = kinds.join(',')
    if (onlyFlagged) p.only_flagged = true
    if (onlyFailed) p.only_failed = true
    if (lang) p.lang = lang
    if (logDays) p.days = logDays
    if (query.trim()) p.q = query.trim()
    if (ticket.trim()) p.ticket = ticket.trim()
    return p
  }, [page, kinds, onlyFlagged, onlyFailed, lang, logDays, query, ticket])

  const loadLogs = useCallback(async (silent = false) => {
    if (!silent) setLLoading(true)
    try {
      setLogs(await aiApi.logs(logParams))
      setLError(null)
    } catch (e) {
      setLError(e.message || 'Jurnal yuklanmadi')
    } finally {
      if (!silent) setLLoading(false)
    }
  }, [logParams])

  useEffect(() => { loadAnalytics() }, [loadAnalytics])
  useEffect(() => { loadLogs() }, [loadLogs])

  // Avto-yangilanish faqat jurnal ochiq turganda: analitika og'ir so'rov va
  // uni fonda har daqiqada qayta hisoblash keraksiz.
  useEffect(() => {
    if (tab !== 'log') return undefined
    const id = setInterval(() => loadLogs(true), POLL_MS)
    return () => clearInterval(id)
  }, [tab, loadLogs])

  const refresh = () => { loadAnalytics(); loadLogs() }

  /* ── Tafsilot ── */
  const openDetail = useCallback(async (id) => {
    setDetailOpen(true)
    setDetail(null)
    setDetailLoading(true)
    try {
      setDetail(await aiApi.logDetail(id))
    } catch (e) {
      addToast(e.message || "Yozuv ochilmadi", 'critical')
      setDetailOpen(false)
    } finally {
      setDetailLoading(false)
    }
  }, [addToast])

  /* ── Bayroqcha ── */
  const applyFlag = async (row, flagged, note) => {
    setBusyId(row.id)
    try {
      await aiApi.flagLog(row.id, flagged, note)
      await loadLogs(true)
      setDetail((d) => (d && d.id === row.id ? { ...d, flagged, flag_note: note } : d))
    } catch (e) {
      addToast(e.message || 'Xatolik yuz berdi', 'critical')
    } finally {
      setBusyId(null)
    }
  }

  // Belgini olib tashlash izohsiz bajariladi; belgilashda izoh so'raladi (ixtiyoriy).
  const toggleFlag = (row) => {
    if (row.flagged) { applyFlag(row, false, ''); return }
    setFlagNote('')
    setFlagTarget(row)
  }

  const submitFlag = () => {
    const row = flagTarget
    if (!row) return
    const note = flagNote.trim()
    setFlagTarget(null)
    applyFlag(row, true, note)
  }

  /* ── Analitikadan jurnalga o'tish (bosiladigan taqsimotlar) ── */
  const goJournal = (patch = {}) => {
    setKinds(patch.kinds || [])
    setOnlyFlagged(Boolean(patch.onlyFlagged))
    setOnlyFailed(Boolean(patch.onlyFailed))
    setLang(patch.lang || '')
    setLogDays(patch.days != null ? String(patch.days) : '')
    setQuery('')
    setTicket('')
    setPage(1)
    setTab('log')
  }

  const resetFilters = () => {
    setKinds([]); setOnlyFlagged(false); setOnlyFailed(false)
    setLang(''); setLogDays(''); setQuery(''); setTicket('')
    setPage(1)
  }

  const filtersOn = kinds.length > 0 || onlyFlagged || onlyFailed || !!lang || !!logDays
    || !!query.trim() || !!ticket.trim()

  return (
    <div className="page-enter">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <h2>AI kuzatuvi</h2>
          <p>Sun'iy intellekt chaqiruvlarining sifati, xarajati va har bir so'rovning to'liq matni</p>
        </div>
        <div className="page-hdr-right">
          <button className="btn-refresh-icon" onClick={refresh} title="Yangilash" aria-label="Yangilash">
            <HiArrowPath aria-hidden="true" />
          </button>
        </div>
      </div>

      <ErrorBox error={tab === 'stats' ? aError : lError} onRetry={refresh} />

      <div style={{ marginBottom: 16 }}>
        <Tabs
          value={tab}
          onChange={setTab}
          items={[
            { id: 'stats', label: 'Analitika', icon: HiChartBar },
            { id: 'log', label: 'Jurnal', icon: HiListBullet, count: logs?.total },
          ]}
        />
      </div>

      {tab === 'stats' ? (
        aLoading && !analytics ? (
          <Loader state="solving" block />
        ) : (
          <AnalyticsView
            a={analytics}
            stats={stats}
            error={aError}
            days={days}
            onDays={setDays}
            onOpenLog={openDetail}
            onGoJournal={goJournal}
          />
        )
      ) : (
        <JournalView
          logs={logs}
          loading={lLoading}
          page={page}
          onPage={setPage}
          kinds={kinds}
          onKinds={(v) => { setKinds(v); setPage(1) }}
          onlyFlagged={onlyFlagged}
          onOnlyFlagged={(v) => { setOnlyFlagged(v); setPage(1) }}
          onlyFailed={onlyFailed}
          onOnlyFailed={(v) => { setOnlyFailed(v); setPage(1) }}
          lang={lang}
          onLang={(v) => { setLang(v); setPage(1) }}
          logDays={logDays}
          onLogDays={(v) => { setLogDays(v); setPage(1) }}
          query={query}
          onQuery={(v) => { setQuery(v); setPage(1) }}
          ticket={ticket}
          onTicket={(v) => { setTicket(v); setPage(1) }}
          filtersOn={filtersOn}
          onReset={resetFilters}
          onOpen={openDetail}
          onToggleFlag={toggleFlag}
          busyId={busyId}
        />
      )}

      <LogDrawer
        open={detailOpen}
        loading={detailLoading}
        detail={detail}
        busy={busyId === detail?.id}
        onClose={() => setDetailOpen(false)}
        onToggleFlag={toggleFlag}
      />

      {/* Bayroqcha izohi — prompt() o'rniga (DESIGN.md 10) */}
      <Modal
        open={!!flagTarget}
        onClose={() => setFlagTarget(null)}
        size="sm"
        title="Javobni yomon deb belgilash"
        footer={
          <>
            <Button variant="secondary" onClick={() => setFlagTarget(null)}>Bekor qilish</Button>
            <Button variant="primary" onClick={submitFlag}>Belgilash</Button>
          </>
        }
      >
        <Field
          label="Izoh (ixtiyoriy)"
          htmlFor={flagNoteId}
          hint="Izohsiz ham belgilash mumkin — bo'sh qoldiring va «Belgilash» ni bosing."
        >
          <Input
            id={flagNoteId}
            value={flagNote}
            onChange={(e) => setFlagNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitFlag() }}
            placeholder="Javobning nimasi noto'g'ri edi?"
            autoFocus
          />
        </Field>
      </Modal>
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────
   ANALITIKA
   ───────────────────────────────────────────────────────────── */

const GRID_2 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16 }

function AnalyticsView({ a, stats, error, days, onDays, onOpenLog, onGoJournal }) {
  const [metric, setMetric] = useState('hajm')

  // `a` har yangilanishda yangi obyekt bo'lib keladi, shuning uchun seriya
  // ham memolanadi — aks holda grafik har renderda qaytadan quriladi.
  const series = useMemo(() => a?.series || [], [a])
  const trendData = useMemo(
    () => series.map((s) => ({ ...s, date: dayLabel(s.date) })),
    [series]
  )
  const m = TREND_METRICS[metric]

  const quality = a?.quality || {}
  const cache = a?.cache || {}
  const funnel = a?.funnel || {}
  const category = a?.category || {}
  const language = a?.language || {}
  const templates = a?.templates || {}
  const solution = a?.solution || {}
  const faq = a?.faq || {}
  const gaps = a?.gaps || {}
  const flow = a?.flow || {}
  const kindsMeta = a?.kinds_meta || {}
  const kindRows = a?.kinds || []

  const periodSelector = (
    <Segmented
      aria-label="Davr"
      value={days}
      onChange={onDays}
      items={PERIODS}
    />
  )

  if (!a) {
    // Xato bo'lganda sababni ErrorBox aytadi — bu yerda uni takrorlamaymiz,
    // lekin "ma'lumot yo'q" degan yolg'on xulosa ham chiqarmaymiz.
    return (
      <EmptyState
        icon={HiSparkles}
        title={error ? "Analitika yuklanmadi" : "Analitika hozircha yo'q"}
        text={error
          ? "Server analitika so'roviga javob bermadi. Yuqoridagi «Qayta urinish» tugmasini bosing."
          : "AI chaqiruvlari boshlangach, bu yerda voronka, xarajat va sifat ko'rsatkichlari paydo bo'ladi."}
      />
    )
  }

  return (
    <div className="ui-col ui-gap-16">
      {/* ── Davr va asosiy raqamlar ── */}
      <Toolbar>
        <span style={{ fontWeight: 600 }}>Davr</span>
        {periodSelector}
        <span className="ui-grow" />
        <span className="ui-muted ui-nums" style={{ fontSize: 'var(--ui-fs-xs)' }}>
          {a.period?.from} — {a.period?.to} · kun chegarasi UTC{a.period?.tz}
        </span>
      </Toolbar>

      <KpiStrip
        items={[
          {
            label: 'Javoblar',
            value: fmtNum(quality.replies || 0),
            icon: HiChatBubbleLeftRight,
            spark: series.map((s) => s.replies || 0),
          },
          {
            label: 'Muvaffaqiyat',
            value: quality.success_rate != null ? fmtPct(quality.success_rate) : '—',
            note: `${quality.failed || 0} ta xato`,
            tone: quality.success_rate != null && quality.success_rate < 95 ? 'critical' : undefined,
          },
          {
            label: 'Belgilangan',
            value: fmtPct(quality.flagged_pct ?? 0),
            icon: HiFlag,
            note: `${quality.flagged || 0} ta yozuv`,
            tone: (quality.flagged_pct || 0) > 10 ? 'warn' : undefined,
          },
          {
            label: 'Davr xarajati',
            value: fmtUsd(kindsMeta.total_cost_usd || 0),
            icon: HiBanknotes,
            note: `yordamchi chaqiruvlar ${fmtPct(kindsMeta.helper_share_pct ?? 0)}`,
            spark: series.map((s) => s.cost_usd || 0),
          },
          {
            label: 'Kesh ulushi',
            value: fmtPct(cache.cache_pct ?? 0),
            icon: HiCpuChip,
            note: `maqsad ${cache.target_pct ?? 60}%`,
            tone: (cache.cache_pct || 0) < 5 ? 'critical' : undefined,
          },
          {
            label: 'Shablon bilan javob',
            value: fmtPct(funnel.sent_pct ?? 0),
            icon: HiRectangleStack,
            note: `${funnel.stages?.[5]?.n ?? 0} / ${funnel.stages?.[0]?.n ?? 0} murojaat`,
          },
        ]}
      />

      {/* ── Kunlik dinamika ── */}
      <Card>
        <TrendChart
          data={trendData}
          height={240}
          series={m.series}
          valueFmt={m.fmt}
          title="Kunlik dinamika"
          sub={`Chaqiruv sanasi bo'yicha · ${a.period?.days} kun`}
          actions={
            <Segmented
              aria-label="Ko'rsatkich"
              value={metric}
              onChange={setMetric}
              items={Object.entries(TREND_METRICS).map(([v, c]) => ({ value: v, label: c.label }))}
            />
          }
        />
      </Card>

      {/* ── Voronka va turkum ── */}
      <div style={GRID_2}>
        <Card title="Shablon voronkasi" icon={HiFunnel}>
          <Funnel
            steps={(funnel.stages || []).map((s) => ({
              key: s.key,
              label: s.label,
              value: s.n,
              tone: s.key === 'sent' && s.n > 0 ? 'ok' : undefined,
            }))}
            note={
              funnel.picker_calls
                ? `Tanlovchi ${funnel.picker_calls.total} marta javob berdi: ${funnel.picker_calls.found} ta shablon topdi, `
                  + `${funnel.picker_calls.rejected} marta «mos shablon yo'q» dedi (${fmtPct(funnel.picker_calls.rejected_pct)}).`
                  // Yiqilgan chaqiruv «rad etdi» EMAS — model umuman javob bermagan
                  + (funnel.picker_calls.failed
                    ? ` Yana ${funnel.picker_calls.failed} chaqiruv xato bilan tugadi (model javob bermagan) — bu rad etish emas.`
                    : '')
                : undefined
            }
          />
          <div className="ui-muted" style={{ fontSize: 'var(--ui-fs-xs)', marginTop: 8 }}>
            Davr bog'lanishi: murojaat yaratilgan sana (kogorta). Foiz — oldingi bosqichdan.
          </div>
        </Card>

        <Card
          title="Turkum tasnifi"
          icon={HiSquares2X2}
          actions={
            <Button size="sm" onClick={() => onGoJournal({ kinds: ['categorize'], days })}>
              Tasniflagich jurnali
            </Button>
          }
        >
          <KpiStrip
            className="ui-lattice--2"
            items={[
              {
                label: 'Turkumsiz murojaat',
                value: fmtPct(category.uncategorized_pct ?? 0),
                note: `${category.uncategorized || 0} / ${category.tickets || 0}`,
                tone: (category.uncategorized_pct || 0) > 50 ? 'critical' : undefined,
              },
              {
                label: 'Tasniflagich rad etdi',
                value: fmtPct(category.classifier?.none_pct ?? 0),
                note: `${category.classifier?.calls || 0} chaqiruvdan ${category.classifier?.none || 0} tasi`,
              },
            ]}
          />
          <div style={{ marginTop: 12 }}>
            <DistributionBar
              label="Turkumlar bo'yicha murojaat"
              items={(category.by_category || []).filter((c) => c.n > 0).map((c) => ({
                key: c.id, label: c.name || '—', value: c.n,
              }))}
              emptyLabel="Davrda turkumlangan murojaat yo'q"
            />
          </div>
          <MiniTable
            title="Model o'ylab topgan turkumlar (bazada yo'q)"
            empty="Yo'q — tasniflagich faqat mavjud turkumlarni aytgan"
            columns={[
              { key: 'name', title: 'Nom' },
              { key: 'n', title: 'Marta', width: 70, align: 'right', render: (r) => <span className="ui-nums">{r.n}</span> },
            ]}
            rows={category.classifier?.invented_names || []}
            rowKey={(r) => r.name}
          />
          <MiniTable
            title="Murojaati bor, lekin faol shabloni yo'q turkumlar"
            empty="Hammasi joyida — har bir ishlatilgan turkumda faol shablon bor"
            columns={[
              { key: 'name', title: 'Turkum' },
              { key: 'tickets', title: 'Murojaat', width: 90, align: 'right', render: (r) => <span className="ui-nums">{r.tickets}</span> },
            ]}
            rows={category.empty_categories || []}
          />
        </Card>
      </div>

      {/* ── Kesh ── */}
      <Card title="Kesh samarasi" icon={HiCpuChip}>
        {(cache.cache_pct || 0) < 5 && (cache.prompt_tokens || 0) > 0 && (
          <Alert tone="warn" title="Kesh ishlamayapti">
            Barqaror prefiks (system prompt) har chaqiruvda to'liq narxda o'qilmoqda.
            Kesh yoqilsa kirish tokeni sezilarli arzonlashadi.
          </Alert>
        )}
        <div className="ui-row ui-gap-16 ui-wrap" style={{ margin: '12px 0', alignItems: 'flex-end' }}>
          <div className="ui-grow" style={{ minWidth: 220 }}>
            <div className="ui-nums" style={{ fontSize: 'var(--ui-fs-xl, 28px)', fontWeight: 600, lineHeight: 1.1 }}>
              {fmtPct(cache.cache_pct ?? 0)}
            </div>
            <div className="ui-muted" style={{ fontSize: 'var(--ui-fs-xs)', marginBottom: 6 }}>
              Kirish tokenlarining necha foizi keshdan o'qildi (maqsad {cache.target_pct ?? 60}%)
            </div>
            <Progress
              value={cache.cache_pct ?? 0}
              tone={(cache.cache_pct || 0) >= (cache.target_pct || 60) ? 'ok' : undefined}
              label="Kesh ulushi"
            />
          </div>
        </div>
        <KpiStrip
          className="ui-lattice--3"
          items={[
            { label: 'Tejalgan', value: fmtUsd(cache.saved_usd || 0), note: 'kesh chegirmasi hisobiga' },
            { label: "Har javobga o'rtacha kirish", value: fmtNum(cache.avg_input_per_reply || 0), note: 'token' },
            { label: 'Javoblar narxi', value: fmtUsd(cache.cost_usd || 0), note: "faqat kind=reply" },
          ]}
        />
        <MiniTable
          title="Model kesimi"
          empty="Davrda model chaqirilmagan"
          columns={[
            { key: 'model', title: 'Model', render: (r) => <span className="ui-mono">{r.model}</span> },
            { key: 'calls', title: 'Chaqiruv', width: 90, align: 'right', render: (r) => <span className="ui-nums">{fmtNum(r.calls)}</span> },
            { key: 'prompt_tokens', title: 'Kirish', width: 90, align: 'right', render: (r) => <span className="ui-nums">{fmtTok(r.prompt_tokens)}</span> },
            { key: 'cache_read', title: 'Kesh', width: 90, align: 'right', render: (r) => <span className="ui-nums">{fmtTok(r.cache_read)}</span> },
            { key: 'cache_pct', title: 'Kesh %', width: 80, align: 'right', render: (r) => <span className="ui-nums">{fmtPct(r.cache_pct)}</span> },
            { key: 'avg_input', title: "O'rt. kirish", width: 100, align: 'right', render: (r) => <span className="ui-nums">{fmtNum(r.avg_input)}</span> },
            { key: 'cost_usd', title: 'Narx', width: 90, align: 'right', render: (r) => <span className="ui-nums">{fmtUsd(r.cost_usd)}</span> },
          ]}
          rows={cache.by_model || []}
          rowKey={(r) => r.model}
        />
      </Card>

      {/* ── Sifat va yechim tezligi ── */}
      <div style={GRID_2}>
        <Card
          title="Sifat belgilari"
          icon={HiFlag}
          actions={
            <Button size="sm" onClick={() => onGoJournal({ onlyFlagged: true, days })}>
              Belgilanganlar
            </Button>
          }
        >
          <KpiStrip
            className="ui-lattice--2"
            items={[
              {
                label: 'Belgilangan javob',
                value: fmtPct(quality.flagged_pct ?? 0),
                note: `${quality.flagged || 0} / ${quality.replies || 0}`,
                tone: (quality.flagged_pct || 0) > 10 ? 'warn' : undefined,
              },
              {
                label: 'Muvaffaqiyat',
                value: quality.success_rate != null ? fmtPct(quality.success_rate) : '—',
                note: `${quality.failed || 0} ta xato (javoblarda)`,
                tone: quality.success_rate != null && quality.success_rate < 95 ? 'critical' : undefined,
              },
            ]}
          />
          <div style={{ marginTop: 12 }}>
            <DistributionBar
              label="Nega belgilandi"
              items={(quality.reasons || []).map((r) => ({
                key: r.key,
                label: r.label,
                value: r.n,
                tone: r.key === 'link_hallucinated' ? 'critical' : undefined,
              }))}
              total={quality.flagged || undefined}
              onSelect={() => onGoJournal({ onlyFlagged: true, days })}
              emptyLabel="Belgilangan javob yo'q"
            />
          </div>
          <MiniTable
            title="Xatolar"
            empty="Davrda muvaffaqiyatsiz chaqiruv yo'q"
            columns={[
              { key: 'error', title: 'Xato matni', render: (r) => <span className="ui-mono" style={{ fontSize: 'var(--ui-fs-xs)' }}>{r.error}</span> },
              { key: 'n', title: 'Marta', width: 70, align: 'right', render: (r) => <span className="ui-nums">{r.n}</span> },
            ]}
            rows={quality.errors_top || []}
            rowKey={(r, i) => `${i}-${r.error}`}
          />
        </Card>

        <Card title="Yechim tezligi" icon={HiClock}>
          <KpiStrip
            className="ui-lattice--2"
            items={[
              {
                label: 'Havola nechanchi javobda',
                value: solution.median_turn ?? '—',
                note: 'mediana',
              },
              {
                label: 'Havola umuman yuborilmagan',
                value: fmtPct(solution.no_link_pct ?? 0),
                note: `${solution.no_link || 0} / ${solution.tickets || 0} murojaat`,
                tone: (solution.no_link_pct || 0) > 25 ? 'warn' : undefined,
              },
            ]}
          />
          <div style={{ marginTop: 12 }}>
            <SolutionChart distribution={solution.distribution || []} />
          </div>
          <div className="ui-muted" style={{ fontSize: 'var(--ui-fs-xs)' }}>
            Kamida 2 ta AI javobi bo'lgan, lekin havola olmagan murojaatlar: {solution.no_link_min2 || 0} ta.
          </div>
        </Card>
      </div>

      {/* ── Til va shablonlar ── */}
      <div style={GRID_2}>
        <Card title="Til va alifbo" icon={HiLanguage}>
          <KpiStrip
            className="ui-lattice--2"
            items={[
              {
                label: 'Alifbo nomuvofiqligi',
                value: fmtPct(language.mismatch_pct ?? 0),
                note: `${language.mismatch || 0} / ${language.replies || 0} javob`,
                tone: (language.mismatch || 0) > 0 ? 'warn' : undefined,
              },
              {
                label: 'Profildan boshqa tilda yozgan',
                value: fmtNum(language.profile_drift || 0),
                note: 'fuqaro',
              },
            ]}
          />
          <MiniTable
            title="Fuqaro yozgan alifbo ↔ javob alifbosi"
            empty="Ma'lumot yo'q"
            columns={[
              { key: 'asked', title: 'Fuqaro', render: (r) => SCRIPT_LABEL[r.asked] || r.asked || '—' },
              { key: 'answered', title: 'Javob', render: (r) => SCRIPT_LABEL[r.answered] || r.answered || '—' },
              {
                key: 'n', title: 'Soni', width: 70, align: 'right',
                render: (r) => (
                  <span className="ui-nums" style={{ color: isScriptBad(r) ? 'var(--ui-st-critical)' : undefined, fontWeight: isScriptBad(r) ? 600 : undefined }}>
                    {r.n}
                  </span>
                ),
              },
            ]}
            rows={language.matrix || []}
            rowKey={(r) => `${r.asked}-${r.answered}`}
          />
          {(language.samples || []).length > 0 && (
            <div className="ui-col ui-gap-4" style={{ marginTop: 12 }}>
              <div className="ui-muted" style={{ fontSize: 'var(--ui-fs-xs)', fontWeight: 600 }}>
                Nomuvofiq javob misollari
              </div>
              <div className="ui-row ui-wrap ui-gap-8">
                {language.samples.map((s) => (
                  <Button key={s.log_id} size="sm" onClick={() => onOpenLog(s.log_id)}>
                    {s.ticket_number || 'Murojaatsiz'} · {SCRIPT_LABEL[s.asked] || s.asked} → {SCRIPT_LABEL[s.answered] || s.answered}
                  </Button>
                ))}
              </div>
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <DistributionBar
              label="Javoblar profil tili bo'yicha"
              items={(stats?.by_lang || []).map((l) => ({
                key: l.lang,
                label: LANG_LABEL[l.lang] || l.lang,
                value: l.count,
                note: l.flagged ? `${l.flagged} ta belgilangan` : undefined,
              }))}
              onSelect={(key) => onGoJournal({ lang: key === '—' ? '' : key, days })}
              emptyLabel="Til kesimi yo'q"
            />
          </div>
        </Card>

        <Card title="Shablonlar" icon={HiRectangleStack}>
          <KpiStrip
            className="ui-lattice--3"
            items={[
              {
                label: 'Hech ishlatilmagan',
                value: fmtPct(templates.never_used_pct ?? 0),
                note: `${templates.never_used || 0} / ${templates.total || 0}`,
                tone: (templates.never_used_pct || 0) > 50 ? 'warn' : undefined,
              },
              {
                label: 'Turkumsiz (tanlanmaydi)',
                value: fmtNum(templates.orphan_count || 0),
                note: 'shablon',
                tone: (templates.orphan_count || 0) > 0 ? 'warn' : undefined,
              },
              {
                label: 'Davrda yuborilgan',
                value: fmtNum(templates.sent_total || 0),
                note: `${templates.active || 0} faol shablondan`,
              },
            ]}
          />
          <div style={{ marginTop: 12 }}>
            <DistributionBar
              label="Eng ko'p ishlatilgan shablonlar"
              items={(templates.top || []).filter((t) => t.use_count > 0).map((t) => ({
                key: t.id,
                label: t.title || '—',
                value: t.use_count,
                note: t.category || undefined,
              }))}
              emptyLabel="Hech bir shablon ishlatilmagan"
            />
          </div>
          <MiniTable
            title="Turkumsiz shablonlar — tanlovchi ularni HECH QACHON ko'rmaydi"
            empty="Hamma shablon turkumga bog'langan"
            columns={[
              { key: 'title', title: 'Sarlavha' },
              {
                key: 'is_active', title: 'Holat', width: 100,
                render: (r) => <Badge tone={r.is_active ? 'ok' : 'neutral'}>{r.is_active ? 'Faol' : 'Qoralama'}</Badge>,
              },
            ]}
            rows={templates.orphan || []}
          />
        </Card>
      </div>

      {/* ── Chaqiruv turlari ── */}
      <Card title="Chaqiruv turlari" icon={HiListBullet}>
        <MiniTable
          empty="Davrda chaqiruv yo'q"
          columns={[
            {
              key: 'kind_label', title: 'Tur', width: 150,
              render: (r) => <Badge tone="neutral">{r.kind_label || r.kind || '—'}</Badge>,
            },
            { key: 'model', title: 'Model', render: (r) => <span className="ui-mono" style={{ fontSize: 'var(--ui-fs-xs)' }}>{r.model}</span> },
            { key: 'calls', title: 'Chaqiruv', width: 90, align: 'right', render: (r) => <span className="ui-nums">{fmtNum(r.calls)}</span> },
            { key: 'input', title: 'Kirish', width: 80, align: 'right', render: (r) => <span className="ui-nums">{fmtTok(r.input)}</span> },
            { key: 'output', title: 'Chiqish', width: 80, align: 'right', render: (r) => <span className="ui-nums">{fmtTok(r.output)}</span> },
            { key: 'cache_read', title: 'Kesh', width: 80, align: 'right', render: (r) => <span className="ui-nums">{fmtTok(r.cache_read)}</span> },
            { key: 'cost_usd', title: 'Narx', width: 90, align: 'right', render: (r) => <span className="ui-nums">{fmtUsd(r.cost_usd)}</span> },
            {
              key: 'success_pct', title: 'Muvaffaqiyat', width: 110, align: 'right',
              render: (r) => (
                <span className="ui-nums" style={{ color: r.failed > 0 ? 'var(--ui-st-critical)' : undefined }}>
                  {fmtPct(r.success_pct)}
                </span>
              ),
            },
          ]}
          rows={kindRows}
          rowKey={(r, i) => `${r.kind}-${r.model}-${i}`}
          onRowClick={(r) => onGoJournal({ kinds: [r.kind], days })}
        />
        <div style={{ marginTop: 12 }}>
          <DistributionBar
            label="Narx tarkibi"
            variant="bar"
            items={aggregateCostByKind(kindRows)}
            valueFmt={fmtUsd}
          />
        </div>
        <div className="ui-muted" style={{ fontSize: 'var(--ui-fs-xs)', marginTop: 8 }}>
          Yordamchi chaqiruvlar (shablon tanlovi + turkumlash) umumiy narxning {fmtPct(kindsMeta.helper_share_pct ?? 0)} ini yeydi.
          Model chaqirilmagan «tekin» javoblar: {fmtNum(kindsMeta.zero_token_replies || 0)} ta ({fmtPct(kindsMeta.zero_token_pct ?? 0)}).
        </div>
      </Card>

      {/* ── FAQ va oqim ── */}
      <div style={GRID_2}>
        <Card title="FAQ qamrovi" icon={HiBookOpen}>
          <KpiStrip
            className="ui-lattice--2"
            items={[
              {
                label: 'Bilim bazasisiz javob',
                value: fmtPct(faq.zero_faq_pct ?? 0),
                note: `${faq.zero_faq || 0} / ${faq.replies || 0}`,
                tone: (faq.zero_faq_pct || 0) > 30 ? 'warn' : undefined,
              },
              { label: "Javobga o'rtacha FAQ", value: String(faq.avg_faq ?? 0).replace('.', ','), note: 'dona' },
            ]}
          />
          <div style={{ marginTop: 12 }}>
            <DistributionBar
              label="Eng ko'p ishlatilgan FAQ"
              items={(faq.top || []).map((f) => ({ key: f.id, label: f.question, value: f.n }))}
              emptyLabel="FAQ ishlatilmagan"
            />
          </div>
          {(faq.uncovered || []).length > 0 && (
            <div className="ui-col ui-gap-4" style={{ marginTop: 12 }}>
              <div className="ui-muted" style={{ fontSize: 'var(--ui-fs-xs)', fontWeight: 600 }}>
                Bilim bazasisiz javob berilgan savollar — FAQ yozish uchun ro'yxat
              </div>
              <div className="ui-col ui-gap-4">
                {faq.uncovered.slice(0, 10).map((u) => (
                  <button
                    key={u.log_id}
                    type="button"
                    className="ui-link"
                    onClick={() => onOpenLog(u.log_id)}
                    style={{
                      textAlign: 'left', background: 'none', border: 'none', padding: 0,
                      cursor: 'pointer', fontSize: 'var(--ui-fs-sm)',
                    }}
                  >
                    <span className="ui-clamp" style={{ '--ui-clamp-lines': 1 }}>{u.question || '(bo\'sh savol)'}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </Card>

        <Card title="Oqim va bo'shliqlar" icon={HiExclamationTriangle}>
          <KpiStrip
            className="ui-lattice--2"
            items={[
              {
                label: 'Operatorga uzatildi',
                value: fmtPct(flow.handoff_pct ?? 0),
                note: `${flow.handed_off || 0} / ${flow.tickets || 0} murojaat`,
              },
              {
                label: 'Uzatishdan keyingi AI xabari',
                value: fmtNum(flow.after_handoff_msgs || 0),
                note: 'kutilgan qiymat 0',
                tone: (flow.after_handoff_msgs || 0) > 0 ? 'critical' : 'ok',
              },
              {
                label: 'Osilgan murojaat',
                value: fmtNum(flow.stuck_count || 0),
                note: 'kutmoqda, operator olmagan',
                tone: (flow.stuck_count || 0) > 0 ? 'warn' : undefined,
              },
              {
                label: 'Ochiq video bo\'shliqlari',
                value: fmtNum(gaps.open || 0),
                note: `${gaps.open_hits || 0} marta so'ralgan`,
              },
            ]}
          />
          <MiniTable
            title="Osilgan murojaatlar"
            empty="Osilgan murojaat yo'q"
            columns={[
              { key: 'ticket_number', title: 'Murojaat', render: (r) => <span className="ui-mono">{r.ticket_number || '—'}</span> },
              {
                key: 'waiting_hours', title: 'Kutmoqda', width: 110, align: 'right',
                render: (r) => <span className="ui-nums">{r.waiting_hours != null ? `${r.waiting_hours} soat` : '—'}</span>,
              },
            ]}
            rows={flow.stuck || []}
            rowKey={(r) => r.ticket_id}
          />
          <MiniTable
            title="Eng ko'p so'ralgan bo'shliqlar"
            empty="Ochiq bo'shliq yo'q"
            columns={[
              { key: 'query_text', title: "So'rov" },
              { key: 'hit_count', title: 'Marta', width: 70, align: 'right', render: (r) => <span className="ui-nums">{r.hit_count}</span> },
            ]}
            rows={gaps.top || []}
            rowKey={(r, i) => `${i}-${r.query_text}`}
          />
        </Card>
      </div>
    </div>
  )
}

/** Nomuvofiq katak: fuqaro va javob alifbosi ikkalasi ham aniq, lekin har xil. */
function isScriptBad(r) {
  return r.asked && r.answered && r.asked !== 'noaniq' && r.answered !== 'noaniq' && r.asked !== r.answered
}

/** Narx tarkibi: `kinds` qatorlari model bo'yicha bo'lingan, turga yig'amiz. */
function aggregateCostByKind(rows) {
  const acc = new Map()
  for (const r of rows || []) {
    const cur = acc.get(r.kind) || { key: r.kind, label: r.kind_label || r.kind, value: 0 }
    cur.value += Number(r.cost_usd) || 0
    acc.set(r.kind, cur)
  }
  return [...acc.values()].filter((x) => x.value > 0).sort((a, b) => b.value - a.value)
}

/**
 * Kichik jadval — kartochka ichidagi ikki-uch ustunli ro'yxat uchun.
 * Bo'sh bo'lsa jadval o'rniga bir qatorlik matn chiqadi: bo'sh jadval
 * "ma'lumot bor, lekin filtrga tushmadi" degan yolg'on taassurot beradi.
 */
function MiniTable({ title, columns, rows = [], empty, rowKey, onRowClick }) {
  return (
    <div className="ui-col ui-gap-4" style={{ marginTop: 12 }}>
      {title && (
        <div className="ui-muted" style={{ fontSize: 'var(--ui-fs-xs)', fontWeight: 600 }}>{title}</div>
      )}
      {rows.length === 0 ? (
        <div className="ui-muted" style={{ fontSize: 'var(--ui-fs-xs)' }}>{empty}</div>
      ) : (
        <Table dense columns={columns} rows={rows} rowKey={rowKey} onRowClick={onRowClick} />
      )}
    </div>
  )
}

/** Havolagacha nechanchi javob — ustunli taqsimot. "Yo'q" ustuni kritik ohangda. */
function SolutionChart({ distribution }) {
  const t = useThemeTokens()
  const ink = toneColor(t, 'ink')
  const bad = toneColor(t, 'critical')
  const data = (distribution || []).map((d) => ({
    nom: d.turn,
    soni: d.n,
    yomon: d.turn === "yo'q",
  }))
  const empty = data.every((d) => !d.soni)

  return (
    <ChartFrame
      height={200}
      empty={empty}
      emptyText="Davrda AI javobi bo'lgan murojaat yo'q"
      legend={[
        { name: 'Havola shu javobda kelgan', color: ink },
        { name: 'Havola umuman yo\'q', color: bad },
      ]}
      legendVariant="area"
    >
      <BarChart data={data} margin={{ top: 16, right: 8, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={t['--ui-border']} vertical={false} />
        <XAxis dataKey="nom" tick={{ fontSize: 11, fill: t['--ui-text-2'] }} tickLine={false} stroke={t['--ui-border']} />
        <YAxis width={40} allowDecimals={false} tick={{ fontSize: 11, fill: t['--ui-text-2'] }} tickLine={false} axisLine={false} />
        <RcTooltip content={<ChartTooltip />} cursor={{ fill: t['--ui-surface-2'] }} />
        <Bar dataKey="soni" name="Murojaat" radius={[2, 2, 0, 0]}>
          {data.map((d) => <Cell key={d.nom} fill={d.yomon ? bad : ink} />)}
          <LabelList dataKey="soni" position="top" fill={t['--ui-text-3']} fontSize={11} />
        </Bar>
      </BarChart>
    </ChartFrame>
  )
}

/* ─────────────────────────────────────────────────────────────
   JURNAL
   ───────────────────────────────────────────────────────────── */

function JournalView({
  logs, loading, page, onPage,
  kinds, onKinds, onlyFlagged, onOnlyFlagged, onlyFailed, onOnlyFailed,
  lang, onLang, logDays, onLogDays, query, onQuery, ticket, onTicket,
  filtersOn, onReset, onOpen, onToggleFlag, busyId,
}) {
  const available = logs?.kinds_available || []
  const totals = logs?.totals || {}
  const items = logs?.items || []

  const toggleKind = (k) => {
    onKinds(kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k])
  }

  const columns = [
    {
      key: 'created_at', title: 'Vaqt', width: 96,
      render: (r) => <span className="ui-nums ui-muted">{fmtTime(r.created_at)}</span>,
    },
    {
      // `kind_label` — backend beradigan o'zbekcha nom; kelmasa xom `kind`
      // ko'rsatiladi, chunki bo'sh nishon qatorni o'qib bo'lmaydigan qiladi.
      key: 'kind', title: 'Tur', width: 132,
      render: (r) => <Badge tone="neutral">{r.kind_label || r.kind || '—'}</Badge>,
    },
    {
      key: 'ticket_number', title: 'Murojaat', width: 116,
      render: (r) => <span className="ui-mono" style={{ fontSize: 'var(--ui-fs-xs)' }}>{r.ticket_number || '—'}</span>,
    },
    {
      key: 'question', title: 'Savol va javob',
      render: (r) => (
        <div className="ui-col ui-gap-4" style={{ minWidth: 260 }}>
          <span className="ui-clamp" style={{ '--ui-clamp-lines': 2 }}>
            {r.question || <span className="ui-muted">(savol yo'q)</span>}
          </span>
          {r.answer && (
            <span className="ui-clamp ui-muted" style={{ '--ui-clamp-lines': 2, fontSize: 'var(--ui-fs-xs)' }}>
              {r.answer}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'lang', title: 'Til', width: 78,
      render: (r) => <span style={{ fontSize: 'var(--ui-fs-xs)' }}>{LANG_LABEL[r.lang] || r.lang || '—'}</span>,
    },
    {
      key: 'model', title: 'Model', width: 150,
      render: (r) => <span className="ui-mono ui-truncate" style={{ fontSize: 'var(--ui-fs-xs)', display: 'block' }}>{r.model || '—'}</span>,
    },
    {
      key: 'tokens', title: 'Token (kirish/chiqish)', width: 130, align: 'right',
      render: (r) => (
        <span className="ui-nums" style={{ fontSize: 'var(--ui-fs-xs)' }}>
          {fmtTok(r.input_tokens)} / {fmtTok(r.output_tokens)}
          {r.cache_read_tokens > 0 && <span className="ui-muted"> · kesh {fmtTok(r.cache_read_tokens)}</span>}
        </span>
      ),
    },
    {
      key: 'cost_usd', title: 'Narx', width: 88, align: 'right',
      render: (r) => <span className="ui-nums" style={{ fontSize: 'var(--ui-fs-xs)' }}>{fmtUsd(r.cost_usd)}</span>,
    },
    {
      key: 'state', title: 'Holat', width: 130,
      render: (r) => (
        <div className="ui-row ui-wrap ui-gap-4">
          {!r.success && <Badge tone="critical">Xato</Badge>}
          {r.flagged && <Badge tone="medium" icon={HiFlag}>Belgilangan</Badge>}
          {r.faq_count > 0 && <Badge tone="neutral" icon={HiBookOpen}>{r.faq_count}</Badge>}
          {r.prompt_state === 'template' && <Badge tone="neutral">Shablon</Badge>}
        </div>
      ),
    },
    {
      key: 'flag', title: '', width: 44,
      render: (r) => (
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          icon={r.flagged ? HiFlag : HiOutlineFlag}
          disabled={busyId === r.id}
          aria-label={r.flagged ? 'Belgini olib tashlash' : 'Yomon deb belgilash'}
          title={r.flagged ? 'Belgini olib tashlash' : 'Yomon deb belgilash'}
          onClick={(e) => { e.stopPropagation(); onToggleFlag(r) }}
        />
      ),
    },
  ]

  return (
    <div className="ui-col ui-gap-12">
      <Toolbar>
        <SearchInput
          value={query}
          onChange={onQuery}
          placeholder="Savol yoki javob matni bo'yicha qidirish"
        />
        <Input
          size="sm"
          value={ticket}
          onChange={(e) => onTicket(e.target.value)}
          placeholder="Murojaat raqami"
          aria-label="Murojaat raqami bo'yicha filtr"
          style={{ width: 160 }}
        />
        <Select
          value={lang}
          onChange={(e) => onLang(e.target.value)}
          options={LANG_OPTIONS}
          aria-label="Til"
          style={{ width: 150 }}
        />
        <Select
          value={logDays}
          onChange={(e) => onLogDays(e.target.value)}
          options={LOG_DAY_OPTIONS}
          aria-label="Davr"
          style={{ width: 140 }}
        />
        {/* Ikkalasi BIRGA qo'llanadi (VA) — ilgari bir-birini istisno qiluvchi
            tugmalar edi va "belgilangan xatolar" ni ko'rib bo'lmasdi. */}
        <Checkbox checked={onlyFlagged} onChange={(e) => onOnlyFlagged(e.target.checked)} label="Belgilangan" />
        <Checkbox checked={onlyFailed} onChange={(e) => onOnlyFailed(e.target.checked)} label="Xatolar" />
        {filtersOn && <Button size="sm" onClick={onReset}>Filtrni tozalash</Button>}
        <span className="ui-grow" />
        <span className="ui-nums ui-muted" style={{ fontSize: 'var(--ui-fs-xs)' }}>
          {fmtNum(logs?.total || 0)} yozuv · {fmtUsd(totals.cost_usd || 0)} · kirish {fmtTok(totals.input_tokens || 0)}
          {totals.failed > 0 && ` · ${totals.failed} xato`}
          {totals.flagged > 0 && ` · ${totals.flagged} belgilangan`}
        </span>
      </Toolbar>

      {/* Tur chiplari — `kinds_available` dan quriladi, ko'p tanlov mumkin */}
      <div className="ui-row ui-wrap ui-gap-8">
        <Button
          size="sm"
          variant={kinds.length === 0 ? 'primary' : 'secondary'}
          onClick={() => onKinds([])}
        >
          Hammasi
        </Button>
        {available.map((k) => (
          <Button
            key={k.kind}
            size="sm"
            variant={kinds.includes(k.kind) ? 'primary' : 'secondary'}
            onClick={() => toggleKind(k.kind)}
          >
            {k.label} · {k.count}
          </Button>
        ))}
      </div>

      <Table
        columns={columns}
        rows={items}
        loading={loading && !logs}
        onRowClick={(r) => onOpen(r.id)}
        empty={
          <EmptyState
            icon={HiSparkles}
            title={filtersOn ? 'Filtrga mos yozuv yo\'q' : "Hozircha AI chaqiruvlari yo'q"}
            text={filtersOn
              ? 'Filtrlarni bo\'shatib ko\'ring — jurnal barcha turdagi chaqiruvlarni saqlaydi.'
              : 'Avto AI operator yoqilganda va murojaatlar kelganda, chaqiruvlar shu yerda ko\'rinadi.'}
            action={filtersOn ? <Button size="sm" onClick={onReset}>Filtrni tozalash</Button> : undefined}
          />
        }
      />

      {(logs?.total || 0) > PER_PAGE && (
        <Pagination page={page} total={logs.total} perPage={PER_PAGE} onChange={onPage} />
      )}
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────
   TAFSILOT PANELI
   ───────────────────────────────────────────────────────────── */

/**
 * Bitta chaqiruvning to'liq tarkibi.
 *
 * Kenglik ATAYLAB kitning 480px idan katta: bu yerda 30 000 belgilik
 * system prompt o'qiladi, tor ustunda u o'qib bo'lmaydigan tasmaga aylanadi.
 */
function LogDrawer({ open, loading, detail, busy, onClose, onToggleFlag }) {
  const [tab, setTab] = useState('io')

  // Boshqa yozuv ochilganda birinchi bo'limga qaytamiz
  useEffect(() => { if (detail?.id) setTab('io') }, [detail?.id])

  if (!open) return null

  const req = detail?.request || {}
  const res = detail?.response || {}
  const usage = detail?.usage || {}
  const messages = req.messages || []
  // v2 suratdagi qo'shimcha bloklar (eski yozuvlarda bo'lmaydi)
  const post = detail?.post || null
  const attempt = detail?.attempt || null

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={detail ? `${detail.kind_label} · ${detail.ticket_number || 'murojaatsiz'}` : 'Yuklanmoqda…'}
      style={{ width: 'min(940px, 100%)' }}
      footer={
        <div className="ui-row ui-gap-8" style={{ width: '100%' }}>
          {detail && (
            <Button
              variant={detail.flagged ? 'secondary' : 'danger'}
              size="sm"
              icon={detail.flagged ? HiOutlineFlag : HiFlag}
              loading={busy}
              onClick={() => onToggleFlag({ id: detail.id, flagged: detail.flagged })}
            >
              {detail.flagged ? 'Belgini olib tashlash' : 'Yomon deb belgilash'}
            </Button>
          )}
          <span className="ui-grow" />
          <Button variant="secondary" size="sm" onClick={onClose}>Yopish</Button>
        </div>
      }
    >
      {loading || !detail ? (
        <Loader state="solving" block />
      ) : (
        <div className="ui-col ui-gap-12">
          {detail.prompt_state === 'template' && (
            <Alert tone="info" title="Model chaqirilmagan">
              Fuqaroga admin tasdiqlagan shablon yuborilgan — shuning uchun prompt ham, token sarfi ham yo'q.
            </Alert>
          )}
          {detail.prompt_state === 'none' && (
            <Alert tone="info" title="Prompt saqlanmagan">
              Bu yozuv prompt saqlash yoqilishidan OLDIN yozilgan. Yangi yozuvlarda
              barcha turdagi chaqiruvlar — tanlovchi, tasniflagich, ovoz, tarjima,
              signalizatsiya — prompti bilan to'liq saqlanadi.
            </Alert>
          )}
          {!res.success && (
            <Alert tone="danger" title="Chaqiruv muvaffaqiyatsiz">{res.error || 'Xato matni yo\'q'}</Alert>
          )}
          {detail.flagged && (
            <Alert tone="warn" title="Belgilangan javob">
              {detail.flag_note || 'Izohsiz belgilangan'}
              {(detail.flag_reasons || []).length > 0 && (
                <div className="ui-row ui-wrap ui-gap-4" style={{ marginTop: 6 }}>
                  {detail.flag_reasons.map((r) => <Badge key={r.key} tone="medium">{r.label}</Badge>)}
                </div>
              )}
            </Alert>
          )}

          <DescriptionList
            items={[
              { key: 'Vaqt', value: fmtTime(detail.created_at) },
              { key: 'Provayder', value: detail.provider || '—' },
              { key: 'Model', value: <span className="ui-mono">{detail.model || '—'}</span> },
              { key: 'max_tokens', value: detail.max_tokens != null ? fmtNum(detail.max_tokens) : '—' },
              { key: 'Til (profil)', value: LANG_LABEL[detail.lang] || detail.lang || '—' },
              {
                key: 'Tokenlar',
                value: (
                  <span className="ui-nums">
                    kirish {fmtNum(usage.input_tokens)} · chiqish {fmtNum(usage.output_tokens)}
                    {' · '}kesh {fmtNum(usage.cache_read_tokens)}
                    {' · '}jami prompt {fmtNum(usage.prompt_tokens_total)}
                  </span>
                ),
              },
              { key: 'Narx', value: <span className="ui-nums">{fmtUsd(usage.cost_usd)}</span> },
              {
                key: 'Tugash sababi (stop_reason)',
                value: res.stop_reason
                  ? (
                    <span>
                      <span className="ui-mono">{res.stop_reason}</span>
                      {res.stop_truncated && <> · <Badge tone="warn">javob kesilgan</Badge></>}
                    </span>
                  )
                  : <span className="ui-muted">bu yozuvda yo'q (eski yozuv)</span>,
              },
              {
                key: 'Javob vaqti',
                value: res.latency_ms != null
                  ? <span className="ui-nums">{fmtNum(res.latency_ms)} ms</span>
                  : <span className="ui-muted">—</span>,
              },
              {
                key: 'Ishlatilgan FAQ',
                value: (detail.faqs || []).length
                  ? (
                    <div className="ui-col ui-gap-4">
                      {detail.faqs.map((f) => <span key={f.id}>{f.question_uz}</span>)}
                    </div>
                  )
                  : <span className="ui-muted">yo'q — model bilim bazasisiz javob bergan</span>,
              },
            ]}
          />

          <Tabs
            value={tab}
            onChange={setTab}
            items={PROMPT_TABS.map((t) => (
              t.id === 'messages' ? { ...t, count: messages.length } : t
            ))}
          />

          {tab === 'io' && (
            <div className="ui-col ui-gap-12">
              {req.question_truncated && (
                <Alert tone="warn">Savol 4000 belgiga kesib saqlangan — bu to'liq matn emas.</Alert>
              )}
              <TextViewer value={req.question} label="Fuqaro savoli" mono={false} maxHeight={220} emptyText="(savol yozilmagan)" />
              {res.truncated && (
                <Alert tone="warn">
                  Fuqaroga ketgan javob 4000 belgiga kesib saqlangan.
                  {res.raw_available && ' Modelning xom javobi quyida to\'liqroq saqlangan.'}
                </Alert>
              )}
              <TextViewer value={res.text} label="Fuqaroga ketgan javob" mono={false} maxHeight={420} emptyText="(javob yo'q)" />

              {/* Modelning XOM javobi — post-processinggacha (havola tozalash,
                  [YAKUN]/[SHABLON] markerlarini ajratish). "Model AYNAN nima
                  dedi" savoliga faqat shu javob beradi. */}
              {res.raw_available && res.raw_differs && (
                <>
                  <Alert tone="info" title="Xom javob yuqoridagidan farq qiladi">
                    Quyida modelning post-processinggacha bo'lgan matni.
                    {post?.removed_links?.length > 0
                      && ` Olib tashlangan havolalar: ${post.removed_links.length} ta.`}
                    {post?.markers?.length > 0 && ` Ajratilgan markerlar: ${post.markers.join(', ')}.`}
                  </Alert>
                  <TextViewer value={res.raw_text} label="Model qaytargan XOM javob" mono={false} maxHeight={420} />
                </>
              )}
              {res.raw_available && !res.raw_differs && (
                <div className="ui-muted" style={{ fontSize: 12 }}>
                  Modelning xom javobi fuqaroga ketgan matn bilan bir xil — post-processing hech narsani o'zgartirmagan.
                </div>
              )}

              {/* Zaxira kalitlar tarixi: qaysi urinishda muvaffaqiyat bo'ldi */}
              {attempt && (attempt.index != null || (attempt.prev_errors || []).length > 0) && (
                <DescriptionList
                  items={[
                    { key: 'Kalit urinishi', value: attempt.index != null ? `#${attempt.index + 1}` : '—' },
                    ...((attempt.prev_errors || []).length
                      ? [{ key: 'Oldingi kalitlar xatosi', value: (
                        <div className="ui-col ui-gap-4">
                          {attempt.prev_errors.map((e, i) => <span key={i} className="ui-mono">{e}</span>)}
                        </div>
                      ) }]
                      : []),
                  ]}
                />
              )}
            </div>
          )}

          {tab === 'system' && (
            <div className="ui-col ui-gap-12">
              {req.system_extra && (
                <TextViewer value={req.system_extra} label="Qo'shimcha ko'rsatma (system extra)" mono={false} maxHeight={200} />
              )}
              <TextViewer
                value={req.system_prompt}
                label="System prompt"
                maxHeight={520}
                emptyText="(system prompt saqlanmagan)"
              />
            </div>
          )}

          {tab === 'messages' && (
            <div className="ui-col ui-gap-12">
              {messages.length === 0 ? (
                <div className="ui-muted">Suhbat saqlanmagan.</div>
              ) : messages.map((msg, i) => (
                <TextViewer
                  key={i}
                  label={msg.role === 'user' ? 'Fuqaro (user)' : 'AI (assistant)'}
                  value={typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content, null, 2)}
                  maxHeight={300}
                />
              ))}
            </div>
          )}

          {tab === 'raw' && (
            detail.raw_request ? (
              <TextViewer
                value={JSON.stringify(detail.raw_request, null, 2)}
                label="Provayderga yuborilgan payload"
                meta="backendda qurilgan — mijozda qayta tiklanmaydi"
                maxHeight={520}
              />
            ) : (
              <Alert tone="info">Bu yozuv uchun so'rov tanasi saqlanmagan.</Alert>
            )
          )}
        </div>
      )}
    </Drawer>
  )
}
